import { HttpError } from '@/lib/httpError'

interface ProxyRequest {
  target_url: string
  method: string
  headers?: Record<string, string>
  body?: string
  cookies?: Record<string, string>
}

interface ProxyResponse<T = any> {
  status_code: number
  headers: Record<string, string[]>
  body: T
}

interface ApiResponse<T = any> {
  success: boolean
  data: ProxyResponse<T>
  message?: string
}

/**
 * ProxyService - Proxies HTTP requests through the backend
 * This allows us to make requests to Node-RED and other services
 * that might not be directly accessible from the frontend
 */
class ProxyService {
  private readonly API_BASE = ''

  /**
   * Map HTTP status code to error translation key
   */
  private getErrorKeyByStatus(status: number): string {
    switch (status) {
      case 400:
        return 'errors.badRequest'
      case 401:
        return 'errors.unauthorized'
      case 403:
        return 'errors.forbidden'
      case 404:
        return 'errors.notFound'
      case 500:
      case 502:
      case 503:
      case 504:
        return 'errors.serverError'
      default:
        return 'errors.operationFailed'
    }
  }

  /**
   * Proxy a request through the backend
   * @param request - The proxy request configuration
   * @returns The proxied response
   * @throws HttpError with status code for proper error handling
   */
  async proxy<T = any>(request: ProxyRequest): Promise<T> {
    try {
      const response = await fetch(`${this.API_BASE}/api/v2/proxy`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Send cookies (access_token)
        body: JSON.stringify(request),
      })

      if (!response.ok) {
        const errorKey = this.getErrorKeyByStatus(response.status)

        try {
          const errorText = await response.text()
          console.error('Proxy API Error:', {
            status: response.status,
            error: errorText
          })
        } catch {
          console.error('HTTP Error:', response.status)
        }

        throw new HttpError(errorKey, response.status)
      }

      const result: ApiResponse<T> = await response.json()

      // Check if the API call was successful
      if (!result.success) {
        console.error('Proxy API returned success: false', result)
        throw new Error('errors.operationFailed')
      }

      // Return the body from the proxied response
      return result.data.body
    } catch (error) {
      if (error instanceof HttpError) {
        throw error
      }

      console.error('Error in proxy request:', error)

      if (error instanceof Error && error.message.startsWith('errors.')) {
        throw error
      }

      throw new Error('errors.operationFailed')
    }
  }

  /**
   * Convenience method to GET data through proxy
   * @param targetUrl - The target URL to proxy to
   * @param headers - Optional headers to include
   * @returns The response data
   */
  async get<T = any>(targetUrl: string, headers?: Record<string, string>): Promise<T> {
    return this.proxy<T>({
      target_url: targetUrl,
      method: 'GET',
      headers,
    })
  }

  /**
   * Convenience method to POST data through proxy
   * @param targetUrl - The target URL to proxy to
   * @param body - The request body
   * @param headers - Optional headers to include
   * @returns The response data
   */
  async post<T = any>(targetUrl: string, body?: any, headers?: Record<string, string>): Promise<T> {
    return this.proxy<T>({
      target_url: targetUrl,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
      headers: {
        'Content-Type': 'application/json',
        ...headers,
      },
    })
  }

  /**
   * Convenience method to PUT data through proxy
   * @param targetUrl - The target URL to proxy to
   * @param body - The request body
   * @param headers - Optional headers to include
   * @returns The response data
   */
  async put<T = any>(targetUrl: string, body?: any, headers?: Record<string, string>): Promise<T> {
    return this.proxy<T>({
      target_url: targetUrl,
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
      headers: {
        'Content-Type': 'application/json',
        ...headers,
      },
    })
  }

  /**
   * Convenience method to DELETE through proxy
   * @param targetUrl - The target URL to proxy to
   * @param headers - Optional headers to include
   * @returns The response data
   */
  async delete<T = any>(targetUrl: string, headers?: Record<string, string>): Promise<T> {
    return this.proxy<T>({
      target_url: targetUrl,
      method: 'DELETE',
      headers,
    })
  }
}

export const proxyService = new ProxyService()
export type { ProxyRequest, ProxyResponse }
